import { LICENSE_PRODUCT_SMARTDENT } from './LicenseProductConstant';

// Action type constants for the SMARTDent Standalone license distribution flow
// (HQ -> Corp -> Dealer -> Customer voucher channel). See
// prd-rayteams-manager/docs/references/license-api.md for the spec.
//
// RayTeams DynamoDB key convention (SMARTDent domain) — PR #183 R6 기준:
//   - per-app (HQ-global, 제품/라이선스 롤업)
//       → _id = 제품별 고정 문자열(LicenseProductConstant.js 각 제품의 licenseAppId)
//       예: rayteams-license (allocation records), rayteams-product-package (QR records)
//   - per-tenant (법인 스코프 편집 데이터, 법인별 1:N)
//       → _id = companyId
//       예: rayteams-voucher-template (이 PR)
// 규칙 선택 이유: per-app 테이블은 HQ 가 권한 경계이고 cross-tenant 쿼리/롤업이 잦으며,
// per-tenant 테이블은 법인이 주체이고 자기 법인 데이터만 읽고 쓰는 IDOR 경계 관리가 쉽다.
// 신규 테이블 추가 시 어느 규약인지 상수 옆 한 줄 주석을 남길 것.

export const SmartDentConstant = {
    // B group - read operations
    GET_PACKAGES: 'SMARTDENT_GET_PACKAGES',
    GET_PACKAGE: 'SMARTDENT_GET_PACKAGE',
    GET_CORP_ALLOCATION: 'SMARTDENT_GET_CORP_ALLOCATION',
    GET_DEALER_ALLOCATIONS: 'SMARTDENT_GET_DEALER_ALLOCATIONS',
    GET_DEALER_ALLOCATION: 'SMARTDENT_GET_DEALER_ALLOCATION',
    GET_MY_RECEIVED_ALLOCATION: 'SMARTDENT_GET_MY_RECEIVED_ALLOCATION',

    // E group - customer license management (list + suspend/resume toggle)
    GET_CUSTOMER_LICENSES: 'SMARTDENT_GET_CUSTOMER_LICENSES',
    UPDATE_CUSTOMER_LICENSE: 'SMARTDENT_UPDATE_CUSTOMER_LICENSE',

    // D group - QR issuance
    ISSUE_QR: 'SMARTDENT_ISSUE_QR',

    // C group - allocation writes (corp -> dealer)
    UPDATE_DEALER_ALLOCATION: 'SMARTDENT_UPDATE_DEALER_ALLOCATION',

    // F group - FEAT-022 additions
    GET_CORP_CUSTOMER_LICENSES: 'SMARTDENT_GET_CORP_CUSTOMER_LICENSES',
    GET_DEALER_CUSTOMER_LICENSES: 'SMARTDENT_GET_DEALER_CUSTOMER_LICENSES',
    GET_ISSUED_QRS_BY_DEALER: 'SMARTDENT_GET_ISSUED_QRS_BY_DEALER',

    // G group - FEAT-023 voucher template (SPEC-RG-05)
    GET_VOUCHER_TEMPLATE: 'SMARTDENT_GET_VOUCHER_TEMPLATE',
    SAVE_VOUCHER_TEMPLATE: 'SMARTDENT_SAVE_VOUCHER_TEMPLATE',
    RESET_VOUCHER_TEMPLATE: 'SMARTDENT_RESET_VOUCHER_TEMPLATE',

    // H group - FEAT-024 allocation history (SPEC-RG-01 AC-3 / SPEC-RG-02 AC-7)
    GET_GRANT_HISTORY: 'SMARTDENT_GET_GRANT_HISTORY',
    GET_DEALER_ALLOCATION_HISTORY: 'SMARTDENT_GET_DEALER_ALLOCATION_HISTORY',
    APPEND_ALLOCATION_AUDIT: 'SMARTDENT_APPEND_ALLOCATION_AUDIT',

    // I group - FEAT-033 PC Seat activation (localhost:8008 + BE /pcs endpoints)
    SET_RAYTEAMS_INSTALLED: 'SMARTDENT_SET_RAYTEAMS_INSTALLED',
    SET_RAYTEAMS_USER: 'SMARTDENT_SET_RAYTEAMS_USER',
    SET_LOCAL_LICENSE_STATUS: 'SMARTDENT_SET_LOCAL_LICENSE_STATUS',
    SET_MATCHED_VOUCHER: 'SMARTDENT_SET_MATCHED_VOUCHER', // @deprecated IMP-151 AC-3/6: 미사용(PCActivationPanel useMemo 대체). 호환 보존.
    GET_PC_ACTIVATIONS: 'SMARTDENT_GET_PC_ACTIVATIONS',
    SET_PC_ACTIVATION_LOADING: 'SMARTDENT_SET_PC_ACTIVATION_LOADING',
    SET_PC_ACTIVATION_ERROR: 'SMARTDENT_SET_PC_ACTIVATION_ERROR',
    // IMP-175: PC 권장 사양 체크 결과 ({ ok, failures, measured } 또는 null)
    SET_SYSTEM_SPEC_CHECK: 'SMARTDENT_SET_SYSTEM_SPEC_CHECK',

    // J group - FEAT-050 S5a: N단계 서브트리 할당 노드 fan-out 조회
    GET_ALLOCATION_NODES: 'SMARTDENT_GET_ALLOCATION_NODES',

    // IMP-420(#354) 문제 1: resolveScopeContext(thunk SSoT) 판정 결과를 store 로 내려
    // FE 자체 isSubEntity 판정(smartDentQuota.resolveEffectiveCorpBudget)의 대체 근거로 쓴다.
    GET_SCOPE_CONTEXT: 'SMARTDENT_GET_SCOPE_CONTEXT',
};


// Known SMARTDent Standalone package ids. To add packages, extend `packageIds`
// of each product in LicenseProductConstant.js.
// @deprecated FEAT-056: 새 코드는 LicenseProductConstant 의 제품 목록을 쓴다.
export const SMARTDENT_PACKAGE_IDS = LICENSE_PRODUCT_SMARTDENT.packageIds;

// DynamoDB rayteams-license table _id for SMARTDent Standalone allocation records (per-app).
// Allocation records (corp / dealer) live under this app ID, NOT under the
// product package ID from rayteams-product. QR records in rayteams-product-package
// continue to use the product package ID as _id (per-app).
// @deprecated FEAT-056: 새 코드는 LicenseProductConstant 의 제품 목록을 쓴다.
export const SMARTDENT_LICENSE_APP_ID = LICENSE_PRODUCT_SMARTDENT.licenseAppId;

// BUG-110 (2026-05-11): rayteams 측 localhost License Check API
// (`http://localhost:8008/api/license/check/{appId}`) 의 path segment.
// Confluence "Local License Check API doc" v20 / 2026-05-11 기준 변경:
//   smartdent_standalone → license-segmentation
// DDB rayteams-license 테이블의 `_id` (SMARTDENT_LICENSE_APP_ID = 'smartdent_standalone')
// 와 별개 — DDB key 는 유지, localhost API path 만 새 값 사용. 두 식별자가 의미상 분리됨.
// PR #225 review (Suggestion): 단일 위치 SSoT 로 관리하여 향후 rayteams 측 추가 변경 시
// 범위를 명확히 한다.
// @deprecated FEAT-056: 새 코드는 LicenseProductConstant 의 제품 목록(pcActivation.localAppId)을 쓴다.
export const RAYTEAMS_LOCAL_APP_ID = LICENSE_PRODUCT_SMARTDENT.pcActivation.localAppId;
